import { ConfigurationValues, JSONRecordData } from '../Log/graph.util';
import {
  Alert,
  Box,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Modal,
  Select,
  TextField,
  Typography,
  useTheme
} from '@mui/material';

import React, { useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Button from '@mui/material/Button';
import { Close as CloseIcon } from '@mui/icons-material';
import MenuItem from '@mui/material/MenuItem';
import axiosConfig from '../../../Resources/axiosConfig';
import { UserContext } from '../../../contexts/userContext';
import { ProductIdContext } from '../../../contexts/ProductIdContextProvider';
import { I_Installation } from 'src/interfaces/InstallationTypes';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';

interface SodistoreHomeConfigurationProps {
  values: JSONRecordData;
  id: number;
  installation: I_Installation;
}

function SodistoreHomeConfiguration(props: SodistoreHomeConfigurationProps) {
  if (props.values === null) {
    return null;
  }

  const device = props.installation.device;

  const OperatingPriorityOptions =
    device === 3 // Growatt
      ? ['LoadPriority', 'BatteryPriority', 'GridPriority']
      : device === 4 // Sinexcel
        ? [
          'SpontaneousSelfUse',
          'TimeChargeDischarge',
          'TimeOfUsePowerPrice',
          'DisasterStandby',
          'ManualControl',
          'PvPriorityCharging',
          'PrioritySellElectricity'
        ]
        : [];

  const [errors, setErrors] = useState({
    minimumSoC: false,
    gridSetPoint: false
  });

  const SetErrorForField = (field_name, state) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field_name]: state
    }));
  };
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [updated, setUpdated] = useState(false);
  const [dateSelectionError, setDateSelectionError] = useState('');
  const [isErrorDateModalOpen, setErrorDateModalOpen] = useState(false);
  const context = useContext(UserContext);
  const { currentUser, setUser } = context;
  const { product, setProduct } = useContext(ProductIdContext);

  const [formValues, setFormValues] = useState<Partial<ConfigurationValues>>({
    minimumSoC: props.values.Config.MinSoc,
    maximumDischargingCurrent: props.values.Config.MaximumChargingCurrent,
    maximumChargingCurrent: props.values.Config.MaximumDischargingCurrent,
    operatingPriority: OperatingPriorityOptions.indexOf(
      props.values.Config.OperatingPriority
    ),
    batteriesCount: props.values.Config.BatteriesCount,
    clusterNumber: props.values.Config.ClusterNumber??1,
    PvNumber: props.values.Config.PvNumber??0,
    timeChargeDischargePower: props.values.Config?.TimeChargeDischargePower ?? 0, // default 0 W
    timeChargeDischargeTime: props.values.Config?.TimeChargeDischargeTime ? dayjs(props.values.Config.TimeChargeDischargeTime).toDate() : dayjs().hour(8).minute(0).second(0).toDate(), // default 08:00
    timeChargeDischargeDate: props.values.Config?.TimeChargeDischargeDate ? dayjs(props.values.Config.TimeChargeDischargeDate).toDate() : dayjs().add(1, 'day').toDate(), // default = tomorrow
    // controlPermission: props.values.Config.ControlPermission??false,
    controlPermission: String(props.values.Config.ControlPermission).toLowerCase() === "true",

    // ...(device === 4
    //   ? {
    //     clusterNumber: props.values.Config.ClusterNumber??1,
    //     PvNumber: props.values.Config.PvNumber??0,
    //     timeChargeDischargePower:
    //       props.values.Config?.TimeChargeDischargePower ?? 0, // default 0 W
    //     timeChargeDischargeTime: props.values.Config?.TimeChargeDischargeTime
    //       ? dayjs(props.values.Config.TimeChargeDischargeTime).toDate()
    //       : dayjs().hour(8).minute(0).second(0).toDate(), // default 08:00
    //     timeChargeDischargeDate: props.values.Config?.TimeChargeDischargeDate
    //       ? dayjs(props.values.Config.TimeChargeDischargeDate).toDate()
    //       : dayjs().add(1, 'day').toDate(), // default = tomorrow
    //   }
    //   : {}),
    // ...(device === 3
    //   ? {
    //     controlPermission: props.values.Config.ControlPermission??true,
    //   }
    //   : {}),
  });

  const handleOperatingPriorityChange = (event) => {
    setFormValues({
      ...formValues,
      ['operatingPriority']: OperatingPriorityOptions.indexOf(
        event.target.value
      )
    });
  };

  const handleSubmit = async (e) => {
    // console.log('asked for', dayjs(formValues.calibrationChargeDate));
    const configurationToSend: Partial<ConfigurationValues> = {
      minimumSoC: formValues.minimumSoC,
      maximumDischargingCurrent: formValues.maximumDischargingCurrent,
      maximumChargingCurrent: formValues.maximumChargingCurrent,
      operatingPriority: formValues.operatingPriority,
      batteriesCount:formValues.batteriesCount,
      clusterNumber:formValues.clusterNumber,
      PvNumber:formValues.PvNumber,
      timeChargeDischargePower: formValues.timeChargeDischargePower,
      timeChargeDischargeTime: formValues.timeChargeDischargeTime,
      timeChargeDischargeDate: formValues.timeChargeDischargeDate,
      controlPermission:formValues.controlPermission
      // ...(device === 4 &&
      //   OperatingPriorityOptions[formValues.operatingPriority] ===
      //   'TimeChargeDischarge' && {
      //     timeChargeDischargePower: formValues.timeChargeDischargePower,
      //     timeChargeDischargeTime: formValues.timeChargeDischargeTime,
      //     timeChargeDischargeDate: formValues.timeChargeDischargeDate
      //   })
    };

    setLoading(true);
    const res = await axiosConfig
      .post(
        `/EditInstallationConfig?installationId=${props.id}&product=${product}`,
        configurationToSend
      )
      .catch((err) => {
        if (err.response) {
          setError(true);
          setLoading(false);
        }
      });

    if (res) {
      setUpdated(true);
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    switch (name) {
      // case 'minimumSoC':
      //   if (
      //     /[^0-9.]/.test(value) ||
      //     isNaN(parseFloat(value)) ||
      //     parseFloat(value) > 30 ||
      //     parseFloat(value) < 10
      //   ) {
      //     SetErrorForField(name, true);
      //   } else {
      //     SetErrorForField(name, false);
      //   }
      //   break;
      case 'minimumSoC': {
        const numValue = parseFloat(value);

        // Check for invalid characters or non-numeric
        if (/[^0-9.]/.test(value) || isNaN(numValue)) {
          SetErrorForField(name, {
            hasError: true,
            message: 'Invalid number format',
          });
          break;
        }

        // Define device-based ranges
        const minsocRanges = {
          3: { min: 10, max: 30 },
          4: { min: 5, max: 100 },
        };

        // Fallback range if device not listed
        const { min, max } = minsocRanges[device] || { min: 10, max: 30 };

        if (numValue < min || numValue > max) {
          SetErrorForField(name, {
            hasError: true,
            message: `Value should be between ${min}-${max}%`,
          });
        } else {
          SetErrorForField(name, { hasError: false, message: '' });
        }
        break;
      }

      case 'gridSetPoint':
        if (/[^0-9.]/.test(value) || isNaN(parseFloat(value))) {
          SetErrorForField(name, true);
        } else {
          SetErrorForField(name, false);
        }
        break;

      default:
        break;
    }

    setFormValues({
      ...formValues,
      [name]: value
    });
  };

  const handleTimeChargeDischargeChange = (name: string, value: any) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOkOnErrorDateModal = () => {
    setErrorDateModalOpen(false);
  };

  return (
    <Container maxWidth="xl">
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="stretch"
        spacing={3}
      >
        {isErrorDateModalOpen && (
          <Modal open={isErrorDateModalOpen} onClose={() => {}}>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 450,
                bgcolor: 'background.paper',
                borderRadius: 4,
                boxShadow: 24,
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <Typography
                variant="body1"
                gutterBottom
                sx={{ fontWeight: 'bold' }}
              >
                {dateSelectionError}
              </Typography>

              <Button
                sx={{
                  marginTop: 2,
                  textTransform: 'none',
                  bgcolor: '#ffc04d',
                  color: '#111111',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
                onClick={handleOkOnErrorDateModal}
              >
                Ok
              </Button>
            </Box>
          </Modal>
        )}

        <Grid item xs={12} md={12}>
          <CardContent>
            <Box
              component="form"
              sx={{
                '& .MuiTextField-root': { m: 1, width: 390 }
              }}
              noValidate
              autoComplete="off"
            >
              <>
                {device === 3 && (
                  <div style={{ marginBottom: '5px' }}>
                    <FormControlLabel
                      labelPlacement="start"
                      control={
                        <Switch
                          name="controlPermission"
                          checked={Boolean(formValues.controlPermission)}
                          onChange={(e) =>
                            setFormValues((prev) => ({
                              ...prev,
                              controlPermission: e.target.checked,
                            }))
                          }
                          sx={{ transform: "scale(1.4)", marginLeft: "15px" }}
                        />
                      }

                      label={
                        <FormattedMessage
                          id="controlPermission"
                          defaultMessage="Control Permission"
                        />
                      }
                    />
                  </div>
                )}

                <div style={{ marginBottom: '5px' }}>
                  <TextField
                    label={
                      <FormattedMessage
                        id="batteriesCount "
                        defaultMessage="Batteries Count"
                      />
                    }
                    name="batteriesCount"
                    value={formValues.batteriesCount}
                    onChange={handleChange}
                    fullWidth
                  />
                </div>

                {device === 4 && (
                  <>
                    <div style={{ marginBottom: '5px' }}>
                      <TextField
                        label={
                          <FormattedMessage
                            id="clusterNumber"
                            defaultMessage="Cluster Number"
                          />
                        }
                        name="clusterNumber"
                        value={formValues.clusterNumber}
                        onChange={handleChange}
                        fullWidth
                      />
                    </div>

                    <div style={{ marginBottom: '5px' }}>
                      <TextField
                        label={
                          <FormattedMessage
                            id="PvNumber"
                            defaultMessage="PV Number"
                          />
                        }
                        name="PvNumber"
                        value={formValues.PvNumber}
                        onChange={handleChange}
                        fullWidth
                      />
                    </div>
                  </>
                )}


                <div style={{ marginBottom: '5px' }}>
                  <TextField
                    label={
                      <FormattedMessage
                        id="minimum_soc "
                        defaultMessage="Minimum SoC (%)"
                      />
                    }
                    name="minimumSoC"
                    value={formValues.minimumSoC}
                    onChange={handleChange}
                    helperText={
                      errors.minimumSoC ? (
                        <span style={{ color: 'red' }}>
                          Value should be between {device === 4 ? '5–100' : '10–30'}%
                        </span>
                      ) : (
                        ''
                      )
                    }
                    fullWidth
                  />
                </div>

                <div style={{ marginBottom: '5px' }}>
                  <TextField
                    label={
                      <FormattedMessage
                        id="maximumChargingCurrent "
                        defaultMessage="Maximum Charging Current"
                      />
                    }
                    name="maximumChargingCurrent"
                    value={formValues.maximumChargingCurrent}
                    onChange={handleChange}
                    fullWidth
                  />
                </div>

                <div style={{ marginBottom: '5px' }}>
                  <TextField
                    label={
                      <FormattedMessage
                        id="maximumDischargingCurrent "
                        defaultMessage="Maximum Discharging Current"
                      />
                    }
                    name="maximumDischargingCurrent"
                    value={formValues.maximumDischargingCurrent}
                    onChange={handleChange}
                    fullWidth
                  />
                </div>

                <div style={{ marginBottom: '5px', marginTop: '10px' }}>
                  <FormControl fullWidth sx={{ marginLeft: 1, width: 390 }}>
                    <InputLabel
                      sx={{
                        fontSize: 14,
                        backgroundColor: 'white'
                      }}
                    >
                      <FormattedMessage
                        id="operating_priority"
                        defaultMessage="Operating Priority"
                      />
                    </InputLabel>
                    <Select
                      value={
                        OperatingPriorityOptions[formValues.operatingPriority]
                      }
                      onChange={handleOperatingPriorityChange}
                    >
                      {OperatingPriorityOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </>

              {/* --- Extra fields for device=4 + TimeChargeDischarge --- */}
              {device === 4 &&
                OperatingPriorityOptions[formValues.operatingPriority] ===
                'TimeChargeDischarge' && (
                  <>
                    {/* Power input */}
                    <div style={{ marginBottom: '5px' }}>
                      <TextField
                        label="Power (W)"
                        name="timeChargeDischargePower"
                        value={formValues.timeChargeDischargePower}
                        onChange={(e) =>
                          handleTimeChargeDischargeChange(e.target.name, e.target.value)
                        }
                        helperText="Enter a positive or negative power value"
                        fullWidth
                      />
                    </div>

                    {/* Time picker */}
                    <div style={{ marginBottom: '5px' }}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <TimePicker
                          ampm={false}
                          label="Time"
                          value={dayjs(formValues.timeChargeDischargeTime)}
                          onChange={(newTime) =>
                            handleTimeChargeDischargeChange(
                              'timeChargeDischargeTime',
                              newTime?.toDate()
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              sx={{
                                marginTop: 2,
                                width: '100%'
                              }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </div>

                    {/* Date picker */}
                    <div style={{ marginBottom: '5px' }}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Date"
                          value={dayjs(formValues.timeChargeDischargeDate)}
                          onChange={(newDate) =>
                            handleTimeChargeDischargeChange(
                              'timeChargeDischargeDate',
                              newDate?.toDate()
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              sx={{
                                marginTop: 2,
                                width: '100%'
                              }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </div>
                  </>
                )}

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: 10
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{
                    marginLeft: '10px'
                  }}
                >
                  <FormattedMessage
                    id="applychanges"
                    defaultMessage="Apply Changes"
                  />
                </Button>
                {loading && (
                  <CircularProgress
                    sx={{
                      color: theme.palette.primary.main,
                      marginLeft: '20px'
                    }}
                  />
                )}

                {updated && (
                  <Alert
                    severity="success"
                    sx={{
                      ml: 1,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    Successfully applied configuration file
                    <IconButton
                      color="inherit"
                      size="small"
                      onClick={() => setUpdated(false)}
                      sx={{ marginLeft: '4px' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Alert>
                )}
                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      ml: 1,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    An error has occurred
                    <IconButton
                      color="inherit"
                      size="small"
                      onClick={() => setError(false)}
                      sx={{ marginLeft: '4px' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Alert>
                )}
              </div>
            </Box>
          </CardContent>
        </Grid>
      </Grid>
    </Container>
  );
}

export default SodistoreHomeConfiguration;
